import { Suspense, lazy } from 'react';
import { Redirect, useLocation } from 'wouter';

import { V3Loading } from './feedback/v3/V3Loading.js';

// P-50 (2026-07-03) — v3 is now the DEFAULT product surface at markets-os.ai/. v3 is deep
// (WorkOS auth, personalized Evo, per-user persistence, governance + consoles all live), so it
// graduates to the front door. Route-level code-split: opening / downloads only the v3 chunk.
const V3Page = lazy(() => import('./feedback/v3/V3Page.js').then((m) => ({ default: m.V3Page })));
// P-DEMO-SHAREABLE — the shareable-demo entry (/demo). Code-split so the front door doesn't pay for it.
const DemoLoginPage = lazy(() => import('./feedback/v3/DemoLoginPage.js').then((m) => ({ default: m.DemoLoginPage })));
// P-PUBLIC-PAGES — the public marketing site (/about). Additive, self-contained, relocatable: it
// changes NO redirects and leaves the P-50 product front door (/), the logged-out P-SANDBOX surface,
// and /demo exactly as they are. Code-split so `/` never pays for the marketing bundle.
const AboutPage = lazy(() => import('./feedback/v3/about/AboutPage.js').then((m) => ({ default: m.AboutPage })));

/**
 * PURE front-door routing decision (P-50). Root `/` is the v3 product surface; EVERY other path
 * (the retired /feedback/v3 canonical, the retired /feedback mocks + /feedback/wN, the old /wN
 * terminal, and any unknown/mistyped path) redirects to `/`. Exported for unit tests — wouter's
 * `location` is the pathname only, so query strings never reach this.
 */
export function isV3Root(location: string): boolean {
  return location === '/';
}

/** P-DEMO-SHAREABLE — the one non-root real route: the shareable-demo persona picker at /demo. */
export function isDemoRoute(location: string): boolean {
  return location === '/demo';
}

/** P-PUBLIC-PAGES — the additive public marketing route at /about (relocatable; front door untouched). */
export function isAboutRoute(location: string): boolean {
  return location === '/about';
}

export function App(): JSX.Element {
  const [location] = useLocation();

  // P-50 — v3 at root. `/` renders the standalone v3 shell directly (v3 owns its own chrome;
  // it has NO wouter path-routing — its nav is Zustand-state-driven — so mounting it at / is
  // clean). wouter's `location` is the pathname only (query string is separate), so `/?symbol=…`
  // still matches here.
  if (isV3Root(location)) {
    return (
      <Suspense fallback={<V3Loading />}>
        <V3Page />
      </Suspense>
    );
  }

  // P-DEMO-SHAREABLE — the shareable-demo entry. Picks a persona → scoped demo login → lands on `/`.
  if (isDemoRoute(location)) {
    return (
      <Suspense fallback={<V3Loading />}>
        <DemoLoginPage />
      </Suspense>
    );
  }

  // P-PUBLIC-PAGES — the public marketing site at /about (additive; the front-door redirect below is
  // unchanged for every other path).
  if (isAboutRoute(location)) {
    return (
      <Suspense fallback={<V3Loading />}>
        <AboutPage />
      </Suspense>
    );
  }

  // P-50 — LEGACY RETIREMENT + deep-link preservation. Everything else redirects to / (v3):
  //   • the old canonical /feedback/v3 (+ subpaths) — external bookmarks + the WorkOS post-login
  //     landing (api WORKOS_APP_REDIRECT=/feedback/v3 → / → v3; the WorkOS callback URI is the
  //     server endpoint /api/auth/callback, unaffected);
  //   • the retired pre-v3 mocks interface /feedback + /feedback/wN (its old modal AuthDialog
  //     sign-in retires with it — a visitor now sees only the v3 WorkOS auth);
  //   • the older /wN live-terminal redirects;
  //   • any unknown / mistyped path (lands on the product, never a 404).
  // The FeedbackApp + W1–W5 mocks + the old terminal chrome remain in-tree, unrouted (archived,
  // reversible) — no hard delete. This is a client-side SPA redirect (nginx already serves
  // index.html at / via try_files fallback; no nginx or WorkOS change is needed).
  return <Redirect to="/" />;
}
